export class RiskDetailsPanel {
  constructor(container, options = {}) {
    this.container = container;
    this.options = options;
    this.currentMine = null;
    this.riskData = null;
    this.init();
  }

  init() {
    this.render();
    this.attachEventListeners();
  }

  render() {
    this.container.innerHTML = `
      <div class="sidebar-header">
        <h3 class="sidebar-title">Risk Assessment Details</h3>
        <button class="sidebar-close-btn" id="riskDetailsSidebarClose" aria-label="Close panel">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="sidebar-content" id="riskDetailsContent">
        ${this.renderSkeletonUI()}
      </div>
    `;
  }

  renderSkeletonUI() {
    return `
      <div class="risk-skeleton">
        <div class="skeleton-header"></div>
        <div class="skeleton-score-large"></div>
        <div class="skeleton-categories">
          <div class="skeleton-category"></div>
          <div class="skeleton-category"></div>
          <div class="skeleton-category"></div>
          <div class="skeleton-category"></div>
          <div class="skeleton-category"></div>
          <div class="skeleton-category"></div>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    const closeBtn = this.container.querySelector('#riskDetailsSidebarClose');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.hide();
      });
    }

    const overlay = document.getElementById('riskDetailsOverlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        this.hide();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible()) {
        this.hide();
      }
    });
  }

  async show(mine) {
    this.currentMine = mine;

    // Show skeleton UI immediately to prevent flash of old content
    const content = this.container.querySelector('#riskDetailsContent');
    if (content) {
      content.innerHTML = this.renderSkeletonUI();
    }

    this.container.classList.add('visible');

    const overlay = document.getElementById('riskDetailsOverlay');
    if (overlay) {
      overlay.classList.add('visible');
    }

    // Fetch detailed risk data
    await this.loadRiskDetails(mine.mine_id);
  }

  async loadRiskDetails(mineId) {
    try {
      console.log('Fetching risk details for mine:', mineId);
      const response = await fetch(`/api/mines/${mineId}/risk-details`);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Risk details loaded successfully for mine:', mineId);
        console.log('API Response full data:', data);
        console.log('API Response mine_status:', data.mine_status);
        this.riskData = data;
      } else {
        // Log detailed error information for debugging
        const errorText = await response.text().catch(() => '');
        console.error('❌ Failed to load risk details:', {
          mineId,
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorBody: errorText.substring(0, 200) // First 200 chars
        });

        // For 404, this mine has no risk assessment - show premium fallback
        // For other errors (5xx), might be temporary - still show fallback but log differently
        if (response.status === 404) {
          console.log('ℹ️ No risk assessment available for mine:', mineId);
        } else if (response.status >= 500) {
          console.error('⚠️ Server error when fetching risk details for mine:', mineId);
        }
        this.riskData = null;
      }

      // Initialize with null/loading state for async sections
      this.earthquakeData = null;
      this.newsData = null;

      // Render mine data immediately with loading spinners for news/seismic
      if (this.riskData) {
        this.updateContent();
      } else {
        this.renderFallbackContent();
      }

      // Fetch earthquake and news data in background (don't block UI)
      this.loadEarthquakeData(mineId).then(() => {
        this.updateSeismicSection();
      });
      this.loadNewsData(mineId).then(() => {
        this.updateNewsSection();
      });

    } catch (error) {
      console.error('Error loading risk details:', error);
      this.showError();
    }
  }

  async loadEarthquakeData(mineId) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(`/api/usgs/earthquakes/smart/${mineId}?days=30`, { signal: controller.signal });
      if (!response.ok) {
        console.warn('Failed to load earthquake data', response.status);
        this.earthquakeData = { earthquakes: [], error: 'fetch_failed' };
        return;
      }
      this.earthquakeData = await response.json();
    } catch (error) {
      const isTimeout = error.name === 'AbortError';
      console.error(isTimeout ? 'Earthquake fetch timed out' : 'Error loading earthquake data:', error);
      this.earthquakeData = { earthquakes: [], error: isTimeout ? 'timeout' : 'fetch_failed' };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async loadNewsData(mineId) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(`/api/gdelt/news/smart/${mineId}?days=365`, { signal: controller.signal });
      if (!response.ok) {
        console.warn('Failed to load news data', response.status);
        this.newsData = { events: [], error: 'fetch_failed' };
        return;
      }
      this.newsData = await response.json();
    } catch (error) {
      const isTimeout = error.name === 'AbortError';
      console.error(isTimeout ? 'News fetch timed out' : 'Error loading news data:', error);
      this.newsData = { events: [], error: isTimeout ? 'timeout' : 'fetch_failed' };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Update just the seismic section after async load
  updateSeismicSection() {
    const section = this.container.querySelector('#seismicSection');
    if (section) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.renderSeismicActivity();
      section.replaceWith(tempDiv.firstElementChild);
      this.attachSeismicRetryListener();
    }
  }

  // Update just the news section after async load
  updateNewsSection() {
    const section = this.container.querySelector('#newsSection');
    if (section) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.renderRecentNews();
      section.replaceWith(tempDiv.firstElementChild);
      this.attachNewsRetryListener();
    }
  }

  attachSeismicRetryListener() {
    const btn = this.container.querySelector('#seismicRetryBtn');
    if (!btn || !this.currentMine) return;
    btn.addEventListener('click', async () => {
      this.earthquakeData = null;
      this.updateSeismicSection();
      await this.loadEarthquakeData(this.currentMine.mine_id);
      this.updateSeismicSection();
    });
  }

  attachNewsRetryListener() {
    const btn = this.container.querySelector('#newsRetryBtn');
    if (!btn || !this.currentMine) return;
    btn.addEventListener('click', async () => {
      this.newsData = null;
      this.updateNewsSection();
      await this.loadNewsData(this.currentMine.mine_id);
      this.updateNewsSection();
    });
  }

  updateContent() {
    const content = this.container.querySelector('#riskDetailsContent');
    if (!content || !this.riskData) return;

    const { aggregate, categories, subcategories, mine_status, commodities } = this.riskData;

    // Build HTML
    const riskLevel = (aggregate.risk_classification || '').toLowerCase();
    const riskStyles = this.getRiskStyles(riskLevel);

    // Check if this is a SUSPENDED mine with EXTREME classification
    // Show explanation for all suspended mines with EXTREME rating
    console.log('RiskDetailsPanel - mine_status:', mine_status);
    console.log('RiskDetailsPanel - classification:', aggregate.risk_classification);
    console.log('RiskDetailsPanel - overall_score:', aggregate.overall_score);

    const isSuspendedOverride = mine_status === 'Suspended' &&
                                 aggregate.risk_classification === 'EXTREME';

    console.log('RiskDetailsPanel - isSuspendedOverride:', isSuspendedOverride);

    content.innerHTML = `
      <div class="risk-aggregate-section">
        <div class="risk-aggregate-score ${riskStyles.class}">
          <span class="risk-score-hero">${parseFloat(aggregate.overall_score).toFixed(1)}</span>
          <span class="risk-classification-label">${aggregate.risk_classification}</span>
          ${aggregate.is_override_applied && aggregate.calculated_base_score ? `
            <span class="risk-calculated-note">(Calculated aggregate: ${parseFloat(aggregate.calculated_base_score).toFixed(2)})</span>
          ` : ''}
        </div>
        ${isSuspendedOverride ? `
        <div class="risk-override-banner">
          <i class="fas fa-exclamation-triangle"></i>
          <strong>SUSPENDED OPERATIONS</strong>
        </div>
        <div class="risk-override-notice">
          <p><strong>Why EXTREME?</strong></p>
          <p>${aggregate.override_reason || 'This mine has suspended operations, resulting in an automatic EXTREME classification.'}</p>
        </div>
        ` : ''}
        <div class="risk-completeness-badge">
          <i class="fas fa-check-circle"></i>
          <span>${aggregate.completeness_percentage}% Data Complete</span>
        </div>
        ${commodities && commodities.length > 0 ? `
        <div class="risk-commodities-badge">
          <i class="fas fa-gem"></i>
          <span>${commodities.join(', ')}</span>
        </div>
        ` : ''}
        <button class="evidence-package-download-btn" id="downloadEvidencePackageBtn">
          <i class="fas fa-file-pdf"></i>
          <span>Download Evidence Package (PDF)</span>
        </button>
        <button class="risk-weighting-toggle" id="weightingSchemeBtn">
          <i class="fas fa-balance-scale"></i>
          <span>View Weighting Methodology</span>
          <i class="fas fa-chevron-down toggle-icon"></i>
        </button>
        <div class="risk-weighting-panel" id="weightingSchemePanel" style="display: none;">
          <h4>How Risk Scores Are Calculated</h4>
          <p class="weighting-description">Category scores combine the average (60%) with the highest subcategory score (40%) to surface concentrated risks. This prevents a single extreme risk factor from being diluted by lower scores in the same category. The aggregate score is then calculated as a weighted sum of all category scores.</p>
          <div class="weighting-list">
            <div class="weighting-item">
              <span class="weighting-category">Governance & Regulatory</span>
              <span class="weighting-value">25%</span>
            </div>
            <div class="weighting-item">
              <span class="weighting-category">Environmental Risks</span>
              <span class="weighting-value">20%</span>
            </div>
            <div class="weighting-item">
              <span class="weighting-category">Conflict & Security</span>
              <span class="weighting-value">20%</span>
            </div>
            <div class="weighting-item">
              <span class="weighting-category">Operational Risks</span>
              <span class="weighting-value">15%</span>
            </div>
            <div class="weighting-item">
              <span class="weighting-category">Illegal Activity</span>
              <span class="weighting-value">10%</span>
            </div>
            <div class="weighting-item">
              <span class="weighting-category">Economic Risks</span>
              <span class="weighting-value">10%</span>
            </div>
          </div>
          <p class="weighting-formula"><strong>Category Score:</strong> (0.6 × Average) + (0.4 × Highest Subcategory)</p>
          <p class="weighting-formula"><strong>Aggregate:</strong> Σ(Category Score × Category Weight)</p>
        </div>
      </div>

      <div class="risk-categories-section">
        <div class="risk-categories-list">
          ${categories.map(cat => this.renderCategory(cat, subcategories)).join('')}
        </div>
        <button class="risk-collapse-all" id="collapseAllBtn" style="display: none;">
          <i class="fas fa-compress-alt"></i>
          Collapse All Categories
        </button>
      </div>

      ${this.renderSeismicActivity()}
      ${this.renderRecentNews()}
    `;

    // Attach event listeners for expandable categories
    this.attachCategoryEventListeners();

    // Attach weighting scheme toggle listener
    this.attachWeightingSchemeListener();

    // Attach evidence package download listener
    this.attachEvidencePackageListener();
  }

  renderCategory(category, subcategories) {
    const catSubcategories = subcategories.filter(s => s.risk_category_id === category.risk_category_id);

    // Calculate average and max of BASE scores for max-anchor blending display
    const baseScores = catSubcategories.map(s => parseFloat(s.base_score));
    const catAvg = baseScores.length > 0
      ? baseScores.reduce((sum, score) => sum + score, 0) / baseScores.length
      : 0;
    const catMax = baseScores.length > 0 ? Math.max(...baseScores) : 0;

    // Apply max-anchor blending: (0.6 × Average) + (0.4 × Maximum)
    const catScore = (0.6 * catAvg) + (0.4 * catMax);

    const catLevel = this.getCategoryRiskLevel(catScore);
    const catStyles = this.getRiskStyles(catLevel);

    // Check if category is elevated (score > 6.0) for contextual alert
    const isElevated = catScore > 6.0;
    const highestSubcategory = catSubcategories.reduce((highest, sub) =>
      parseFloat(sub.base_score) > parseFloat(highest?.base_score || 0) ? sub : highest
    , null);

    return `
      <div class="risk-category-item ${isElevated ? 'risk-category-elevated' : ''}" data-category-id="${category.risk_category_id}">
        <button class="risk-category-header" aria-expanded="false">
          <div class="risk-category-left">
            <i class="fas fa-chevron-right risk-category-toggle" aria-hidden="true"></i>
            <span class="risk-category-name">${category.category_name}</span>
            ${isElevated ? '<i class="fas fa-exclamation-triangle risk-elevated-icon" title="Elevated risk - review subcategories"></i>' : ''}
          </div>
          <div class="risk-category-right">
            <span class="risk-score-value">${catScore.toFixed(1)}</span>
            <span class="risk-score-dot ${catStyles.class}" aria-label="${this.getRiskLabel(catLevel)}"></span>
          </div>
        </button>
        ${isElevated && highestSubcategory ? `
          <div class="risk-category-alert">
            <i class="fas fa-exclamation-triangle"></i>
            <span>Elevated ${category.category_name}: Contains high-severity risk factor (${highestSubcategory.subcategory_name}: ${parseFloat(highestSubcategory.base_score).toFixed(1)}). Review subcategory details.</span>
          </div>
        ` : ''}
        <div class="risk-subcategories-list" style="display: none;" role="region">
          ${catSubcategories.map(sub => this.renderSubcategory(sub)).join('')}
        </div>
      </div>
    `;
  }

  renderSubcategory(subcategory) {
    const baseScore = parseFloat(subcategory.base_score);
    const weightedScore = parseFloat(subcategory.weighted_score);
    const level = this.getCategoryRiskLevel(baseScore);
    const styles = this.getRiskStyles(level);

    return `
      <div class="risk-subcategory-item" data-subcategory-id="${subcategory.risk_subcategory_id}">
        <div class="risk-subcategory-header">
          <div class="risk-subcategory-left">
            <span class="risk-subcategory-name">${subcategory.subcategory_name}</span>
            <span class="risk-subcategory-meta">
              ${subcategory.proximity_level} • ${subcategory.specificity_level}
            </span>
          </div>
          <div class="risk-subcategory-right">
            <span class="risk-subcategory-score">${baseScore.toFixed(1)}</span>
            <span class="risk-score-dot ${styles.class}" aria-label="${this.getRiskLabel(level)}"></span>
            ${subcategory.justification ? `
              <button class="risk-explanation-btn"
                      aria-label="Show explanation for ${subcategory.subcategory_name}"
                      data-subcategory-id="${subcategory.risk_subcategory_id}">
                <i class="fas fa-info-circle"></i>
              </button>
            ` : ''}
          </div>
        </div>
        ${subcategory.justification ? `
          <div class="risk-subcategory-justification" style="display: none;">
            ${subcategory.justification}
          </div>
        ` : ''}
      </div>
    `;
  }

  renderSources(subcategory) {
    // For MVP, we'll use the justification as the primary source
    // In future, this will be replaced with actual GDELT/USGS citations
    if (!subcategory.justification) {
      return `
        <div class="risk-sources-empty">
          <i class="fas fa-info-circle"></i>
          <span>No additional sources available for this subcategory</span>
        </div>
      `;
    }

    return `
      <div class="risk-sources-list">
        <div class="risk-source-item">
          <div class="risk-source-header">
            <span class="risk-source-type">GMR Methodology</span>
            <span class="risk-source-date">Current Assessment</span>
          </div>
          <div class="risk-source-content">
            ${subcategory.justification}
          </div>
          ${subcategory.proximity_level !== 'Direct' ? `
            <div class="risk-source-meta">
              <i class="fas fa-info-circle"></i>
              <span>Proximity: ${subcategory.proximity_level} | Specificity: ${subcategory.specificity_level}</span>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  renderSeismicActivity() {
    // Show loading state while fetching
    if (this.earthquakeData === null) {
      return `
        <div class="risk-data-section" id="seismicSection">
          <h4 class="risk-section-title">
            <i class="fas fa-globe-americas"></i>
            Seismic Activity (Last 30 Days)
          </h4>
          <div class="risk-data-loading">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Loading seismic data...</p>
          </div>
        </div>
      `;
    }

    if (this.earthquakeData.error) {
      const msg = this.earthquakeData.error === 'timeout'
        ? 'Seismic data took too long to load.'
        : 'Couldn’t load seismic data.';
      return `
        <div class="risk-data-section" id="seismicSection">
          <h4 class="risk-section-title">
            <i class="fas fa-globe-americas"></i>
            Seismic Activity (Last 30 Days)
          </h4>
          <div class="risk-data-error">
            <i class="fas fa-exclamation-circle"></i>
            <p>${msg}</p>
            <button type="button" class="risk-data-retry-btn" id="seismicRetryBtn">
              <i class="fas fa-redo"></i> Retry
            </button>
          </div>
        </div>
      `;
    }

    if (this.earthquakeData.earthquakes.length === 0) {
      return `
        <div class="risk-data-section" id="seismicSection">
          <h4 class="risk-section-title">
            <i class="fas fa-globe-americas"></i>
            Seismic Activity (Last 30 Days)
          </h4>
          <div class="risk-data-empty">
            <i class="fas fa-check-circle"></i>
            <p>No significant earthquakes detected within 200km</p>
          </div>
        </div>
      `;
    }

    const { earthquakes } = this.earthquakeData;

    return `
      <div class="risk-data-section" id="seismicSection">
        <h4 class="risk-section-title">
          <i class="fas fa-globe-americas"></i>
          Seismic Activity (Last 30 Days)
          <span class="risk-data-count">${earthquakes.length}</span>
        </h4>
        <div class="risk-data-list">
          ${earthquakes.map(eq => `
            <div class="risk-data-item">
              <div class="risk-data-header">
                <span class="risk-data-magnitude">M${parseFloat(eq.magnitude).toFixed(1)}</span>
                <span class="risk-data-distance">${parseFloat(eq.distance_km).toFixed(0)}km away</span>
                <span class="risk-data-badge risk-badge-${eq.proximity_level?.toLowerCase() || 'region'}">${eq.proximity_level || 'Region'}</span>
              </div>
              <div class="risk-data-details">
                <div class="risk-data-time">
                  <i class="fas fa-clock"></i>
                  ${new Date(eq.event_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                <div class="risk-data-location">
                  <i class="fas fa-map-marker-alt"></i>
                  ${eq.place_description || 'Unknown location'}
                </div>
                ${eq.impact_severity ? `
                  <div class="risk-data-severity">
                    <i class="fas fa-exclamation-triangle"></i>
                    Impact Severity: ${parseFloat(eq.impact_severity).toFixed(1)}/10
                  </div>
                ` : ''}
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  renderRecentNews() {
    // Show loading state while fetching
    if (this.newsData === null) {
      return `
        <div class="risk-data-section" id="newsSection">
          <h4 class="risk-section-title">
            <i class="fas fa-newspaper"></i>
            Recent News & Events
          </h4>
          <div class="risk-data-loading">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Loading news data...</p>
          </div>
        </div>
      `;
    }

    if (this.newsData.error) {
      const msg = this.newsData.error === 'timeout'
        ? 'News took too long to load (GDELT may be rate-limited).'
        : 'Couldn’t load news data.';
      return `
        <div class="risk-data-section" id="newsSection">
          <h4 class="risk-section-title">
            <i class="fas fa-newspaper"></i>
            Recent News & Events
          </h4>
          <div class="risk-data-error">
            <i class="fas fa-exclamation-circle"></i>
            <p>${msg}</p>
            <button type="button" class="risk-data-retry-btn" id="newsRetryBtn">
              <i class="fas fa-redo"></i> Retry
            </button>
          </div>
        </div>
      `;
    }

    if (this.newsData.events.length === 0) {
      return `
        <div class="risk-data-section" id="newsSection">
          <h4 class="risk-section-title">
            <i class="fas fa-newspaper"></i>
            Recent News & Events
          </h4>
          <div class="risk-data-empty">
            <i class="fas fa-info-circle"></i>
            <p>No relevant news articles found</p>
          </div>
        </div>
      `;
    }

    const { events } = this.newsData;

    // Sort by relevance score (highest first), then by severity as tiebreaker
    const sortedEvents = [...events].sort((a, b) => {
      const relevanceA = parseFloat(a.relevance_score) || 0;
      const relevanceB = parseFloat(b.relevance_score) || 0;
      if (relevanceB !== relevanceA) return relevanceB - relevanceA;
      // Tiebreaker: severity score
      const severityA = parseFloat(a.severity_score) || 0;
      const severityB = parseFloat(b.severity_score) || 0;
      return severityB - severityA;
    });

    return `
      <div class="risk-data-section" id="newsSection">
        <h4 class="risk-section-title">
          <i class="fas fa-newspaper"></i>
          Recent News & Events
          <span class="risk-data-count">${sortedEvents.length}</span>
        </h4>
        <div class="risk-data-list">
          ${sortedEvents.map(event => `
            <div class="risk-data-item">
              <div class="risk-data-header">
                <span class="risk-data-badge risk-badge-${event.event_type || 'other'}">${event.event_type || 'News'}</span>
                ${event.relevance_score ? `
                  <span class="risk-data-relevance-inline">Relevance: ${parseFloat(event.relevance_score).toFixed(1)}/10</span>
                ` : ''}
                ${event.severity_score ? `
                  <span class="risk-data-severity-inline">Severity: ${parseFloat(event.severity_score).toFixed(1)}/10</span>
                ` : ''}
              </div>
              <div class="risk-data-title">
                <a href="${event.article_url}" target="_blank" rel="noopener noreferrer">
                  ${event.article_title || 'Untitled Article'}
                  <i class="fas fa-external-link-alt"></i>
                </a>
              </div>
              <div class="risk-data-details">
                <div class="risk-data-time">
                  <i class="fas fa-clock"></i>
                  ${new Date(event.article_published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
                ${event.article_domain ? `
                  <div class="risk-data-source">
                    <i class="fas fa-globe"></i>
                    ${event.article_domain}
                  </div>
                ` : ''}
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  attachCategoryEventListeners() {
    const categoryItems = this.container.querySelectorAll('.risk-category-item');
    let hasExpandedCategories = false;

    categoryItems.forEach(item => {
      const header = item.querySelector('.risk-category-header');
      const toggle = item.querySelector('.risk-category-toggle');
      const subcategoriesList = item.querySelector('.risk-subcategories-list');

      header.addEventListener('click', () => {
        const isExpanded = subcategoriesList.style.display === 'block';

        // Toggle display
        subcategoriesList.style.display = isExpanded ? 'none' : 'block';

        // Rotate chevron icon
        if (isExpanded) {
          toggle.classList.remove('fa-chevron-down');
          toggle.classList.add('fa-chevron-right');
        } else {
          toggle.classList.remove('fa-chevron-right');
          toggle.classList.add('fa-chevron-down');
        }

        // Update aria-expanded attribute
        header.setAttribute('aria-expanded', !isExpanded);

        // Show/hide collapse all button
        hasExpandedCategories = Array.from(categoryItems).some(ci =>
          ci.querySelector('.risk-subcategories-list').style.display === 'block'
        );
        const collapseBtn = this.container.querySelector('#collapseAllBtn');
        if (collapseBtn) {
          collapseBtn.style.display = hasExpandedCategories ? 'flex' : 'none';
        }
      });
    });

    // Attach explanation button event listeners
    const explanationButtons = this.container.querySelectorAll('.risk-explanation-btn');
    explanationButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent category expansion
        const subcategoryItem = btn.closest('.risk-subcategory-item');
        const justificationPanel = subcategoryItem.querySelector('.risk-subcategory-justification');
        const isVisible = justificationPanel.style.display === 'block';

        justificationPanel.style.display = isVisible ? 'none' : 'block';
        btn.classList.toggle('active', !isVisible);
        btn.setAttribute('aria-expanded', !isVisible);
      });
    });

    // Attach collapse all button listener
    const collapseBtn = this.container.querySelector('#collapseAllBtn');
    if (collapseBtn) {
      collapseBtn.addEventListener('click', () => {
        categoryItems.forEach(item => {
          const subcategoriesList = item.querySelector('.risk-subcategories-list');
          const toggle = item.querySelector('.risk-category-toggle');
          const header = item.querySelector('.risk-category-header');

          subcategoriesList.style.display = 'none';
          toggle.classList.remove('fa-chevron-down');
          toggle.classList.add('fa-chevron-right');
          header.setAttribute('aria-expanded', 'false');
        });
        collapseBtn.style.display = 'none';
      });
    }
  }

  getCategoryRiskLevel(score) {
    if (score <= 2.0) return 'minimal';
    if (score <= 4.0) return 'low';
    if (score <= 6.0) return 'medium';
    if (score <= 8.0) return 'high';
    return 'extreme';
  }

  getRiskStyles(riskLevel) {
    const styles = {
      'minimal': { class: 'risk-minimal', textClass: 'risk-minimal-text', label: 'Minimal' },
      'low': { class: 'risk-low', textClass: 'risk-low-text', label: 'Low' },
      'medium': { class: 'risk-medium', textClass: 'risk-medium-text', label: 'Medium' },
      'high': { class: 'risk-high', textClass: 'risk-high-text', label: 'High' },
      'extreme': { class: 'risk-extreme', textClass: 'risk-extreme-text', label: 'Extreme' }
    };
    return styles[riskLevel] || styles.medium;
  }

  getRiskLabel(level) {
    const labels = {
      'minimal': 'Minimal',
      'low': 'Low',
      'medium': 'Medium',
      'high': 'High',
      'extreme': 'Extreme'
    };
    return labels[level] || 'Unknown';
  }

  attachEvidencePackageListener() {
    const btn = this.container.querySelector('#downloadEvidencePackageBtn');
    if (btn && this.currentMine?.mine_id) {
      btn.addEventListener('click', () => {
        const key = sessionStorage.getItem('ep_key') || prompt('Enter access key for Evidence Package:');
        if (key) {
          sessionStorage.setItem('ep_key', key);
          window.open(`/api/mines/${this.currentMine.mine_id}/evidence-package?key=${encodeURIComponent(key)}`, '_blank');
        }
      });
    }
  }

  attachWeightingSchemeListener() {
    const toggleBtn = this.container.querySelector('#weightingSchemeBtn');
    const panel = this.container.querySelector('#weightingSchemePanel');

    if (toggleBtn && panel) {
      toggleBtn.addEventListener('click', () => {
        const isVisible = panel.style.display === 'block';
        panel.style.display = isVisible ? 'none' : 'block';

        // Rotate chevron icon
        const chevron = toggleBtn.querySelector('.toggle-icon');
        if (chevron) {
          chevron.classList.toggle('rotated', !isVisible);
        }

        toggleBtn.setAttribute('aria-expanded', !isVisible);
      });
    }
  }

  renderFallbackContent() {
    const content = this.container.querySelector('#riskDetailsContent');
    if (!content) return;

    const mine = this.currentMine;

    content.innerHTML = `
      <div class="risk-fallback-section">
        <div class="risk-fallback-header">
          <h4>${mine?.mine_name || 'Unknown Mine'}</h4>
          ${mine?.country_name ? `<p class="risk-fallback-location"><i class="fas fa-map-marker-alt"></i> ${mine.country_name}</p>` : ''}
        </div>
        <div class="risk-fallback-notice">
          <i class="fas fa-lock"></i>
          <div>
            <strong>Premium Risk Assessment</strong>
            <p>Detailed risk scoring and analysis is available for premium members.</p>
          </div>
        </div>
        ${mine?.primary_commodity ? `
          <div class="risk-fallback-info">
            <div class="risk-fallback-info-item">
              <span class="info-label">Primary Commodity</span>
              <span class="info-value">${mine.primary_commodity}</span>
            </div>
            ${mine?.status ? `
              <div class="risk-fallback-info-item">
                <span class="info-label">Status</span>
                <span class="info-value">${mine.status}</span>
              </div>
            ` : ''}
            ${mine?.mine_type ? `
              <div class="risk-fallback-info-item">
                <span class="info-label">Mine Type</span>
                <span class="info-value">${mine.mine_type}</span>
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>

      ${this.renderSeismicActivity()}
      ${this.renderRecentNews()}
    `;
  }

  showError() {
    const content = this.container.querySelector('#riskDetailsContent');
    if (content) {
      content.innerHTML = `
        <div class="risk-error">
          <i class="fas fa-exclamation-triangle"></i>
          <p>Unable to load risk details</p>
        </div>
      `;
    }
  }

  hide() {
    this.currentMine = null;
    this.riskData = null;
    this.container.classList.remove('visible');

    const overlay = document.getElementById('riskDetailsOverlay');
    if (overlay) {
      overlay.classList.remove('visible');
    }
  }

  isVisible() {
    return this.container.classList.contains('visible');
  }

  destroy() {
    this.hide();
    this.container.innerHTML = '';
  }
}
